import React, { useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';
import { css } from '@emotion/react';
import { Navigate, useNavigate } from 'react-router-dom';
import Input from '@components/atoms/Inputs/Input';
import PrimaryButton from '@components/atoms/Inputs/Button/Primary';
import { Sub1Button } from '@components/atoms';
import TextFileImport from '@components/molecules/Import/TextFile';
import { dialogContentsState } from '@components/templates';
import { ErrorCode, ErrorMessage } from '@variables/error';
import { useSetRecoilState } from 'recoil';
import { ADMIN_ACCESS_TOKEN_KEY, getFetcher } from '../../../App';
import {
  LicenseRegistrationBody,
  LicenseRegistrationTable,
  ImportWrapper,
  ImportDesc,
  ActivateTypeWrapper,
} from './styles';
import { submitDebounce } from '../../../utils/lib';
import { Calendar } from 'react-date-range';

const RegistrationPage = () => {
  const [shouldFetch, setShouldFetch] = useState(false);
  const [accessToken, setAccessToken] = useState('');
  const [activateDate, setActivateDate] = useState('');
  const [autoActivate, setAutoActivate] = useState(true);
  const [licenseCode, setLicenseCode] = useState('');
  const [isVerifiedLicenseCode, setIsVerifiedLicenseCode] = useState(false);
  const [isFocusedLicenseCode, setIsFocusedLicenseCode] = useState(false);

  const navigate = useNavigate();
  const setDialogContents = useSetRecoilState(dialogContentsState);

  const memoizedParams = useMemo(() => {
    return {
      activateDate,
      autoActivate,
      licenseCode,
    };
  }, [activateDate, autoActivate, licenseCode]);

  useEffect(() => {
    const newToken = localStorage.getItem(ADMIN_ACCESS_TOKEN_KEY);
    if (newToken) {
      setAccessToken(newToken);
    }
  }, []);

  useEffect(() => {
    setIsVerifiedLicenseCode(false);
  }, [licenseCode]);

  const url = 'api/license';
  const { data, error } = useSWR(shouldFetch ? [{ method: 'POST', url, accessToken, body: memoizedParams }] : null);

  if (error) {
    setShouldFetch(false);
  }

  if (data) {
    if (data.code === 'UNAUTHORIZED') return <Navigate to={'/'} replace={true} />;

    setShouldFetch(false);
    if (data.status === 'SUCCESS' && data.code === '200') {
      setDialogContents({
        type: 'plain',
        header: ``,
        body: <>{'License Notification is complete successfully.'}</>,
        footer: (
          <PrimaryButton
            onClick={() => {
              setDialogContents({ type: 'hidden' });
              navigate('/license/history');
            }}
          >
            ok
          </PrimaryButton>
        ),
      });
    } else {
      let header = '';
      let body = '';
      if (ErrorCode[data.code]) {
        header = `${ErrorCode[data.code]}`;
        body = `${ErrorMessage[ErrorCode[data.code]]}`;
      } else {
        header = data.code;
      }

      setDialogContents({
        type: 'error',
        header: `${header}`,
        body: <>{body}</>,
        footer: <PrimaryButton onClick={() => setDialogContents({ type: 'hidden' })}>OK</PrimaryButton>,
      });
    }
  }

  const certification = async () => {
    const certUrl = 'api/license/certify';
    const response = await getFetcher({
      method: 'POST',
      accessToken,
      body: { licenseCode },
    })(certUrl);

    if (response.status === 'SUCCESS' && response.code === '200') {
      setIsVerifiedLicenseCode(true);
      setDialogContents({
        type: 'success',
        header: ``,
        body: <>{'License certified successfully.'}</>,
        footer: <PrimaryButton onClick={() => setDialogContents({ type: 'hidden' })}>OK</PrimaryButton>,
      });
    } else {
      let header = '';
      let body = '';
      if (ErrorCode[response.code]) {
        header = `${ErrorCode[response.code]}`;
        body = `${ErrorMessage[ErrorCode[response.code]]}`;
      } else {
        header = response.code;
      }

      setDialogContents({
        type: 'error',
        header: `${header}`,
        body: <>{body}</>,
        footer: <PrimaryButton onClick={() => setDialogContents({ type: 'hidden' })}>OK</PrimaryButton>,
      });
    }
  };

  const registration = () => {
    setShouldFetch(true);
  };

  const getLicenseInputState = () => {
    if (isVerifiedLicenseCode && !isFocusedLicenseCode) {
      return 'complete';
    }

    if (isFocusedLicenseCode) {
      return 'active';
    } else {
      return 'wait';
    }
  };

  const getActivateTypeState = () => {
    if (isVerifiedLicenseCode && !isFocusedLicenseCode) {
      if (autoActivate) {
        return 'complete';
      } else {
        if (!!activateDate.length) {
          return 'complete';
        } else {
          return 'active';
        }
      }
    } else {
      return 'code';
    }
  };

  return (
    <>
      <label>Registration</label>
      <LicenseRegistrationBody data-page-type={'table-only'}>
        <LicenseRegistrationTable>
          <thead>
            <tr>
              <th data-type={'code'} data-status={getLicenseInputState()}>
                1. Enter the License Code
              </th>
              <th data-type={'activate'} data-status={getActivateTypeState()}>
                2. Select Activate Type
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <ImportWrapper>
                  <ImportDesc>*Enter the code(copy & paste) or upload the txt file.</ImportDesc>
                  <TextFileImport
                    licenseCode={licenseCode}
                    setLicenseCode={setLicenseCode}
                    onFocus={() => setIsFocusedLicenseCode(true)}
                    onBlur={() => setIsFocusedLicenseCode(false)}
                  />
                  <Sub1Button
                    onClick={submitDebounce(certification)}
                    styles={css`
                      width: 127px;
                      height: 36px;
                      font-size: 14px;
                    `}
                  >
                    Certify
                  </Sub1Button>
                </ImportWrapper>
              </td>
              <td>
                <ActivateTypeWrapper>
                  <Input
                    type={'radio'}
                    name={'activate-type'}
                    checked={getLicenseInputState() === 'complete' ? autoActivate : false}
                    onChange={(e) => setAutoActivate(true)}
                  >
                    Auto – activate (renew upon expiration)
                  </Input>
                  <Input
                    type={'radio'}
                    name={'activate-type'}
                    checked={getLicenseInputState() === 'complete' ? !autoActivate : false}
                    onChange={() => setAutoActivate(false)}
                  >
                    Manual - activate
                    <Input
                      type={'date'}
                      value={activateDate}
                      onChange={(e) => setActivateDate(e.target.value)}
                      styles={css`
                        display: flex;
                        justify-content: center;
                        align-items: center;
                      `}
                    />
                  </Input>
                </ActivateTypeWrapper>
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={2}>
                <PrimaryButton
                  styles={css`
                    width: 188px;
                    height: 48px;
                    font-size: 18px;
                  `}
                  disabled={getLicenseInputState() !== 'complete' || getActivateTypeState() !== 'complete'}
                  onClick={submitDebounce(registration)}
                >
                  Registration
                </PrimaryButton>
              </td>
            </tr>
          </tfoot>
        </LicenseRegistrationTable>
      </LicenseRegistrationBody>
    </>
  );
};

export default RegistrationPage;
